import Styles from './BrandProfileWebsite.module.scss'
import { Input } from 'almanac-ui/TextInputField/Input'
import { useState } from 'react'
import { Button } from 'blaze-ui/Button'
import { ChevronRight } from 'blaze-ui/icons/24'
import { useCreateBrandProfile } from 'hooks/queries/brandProfiles/useCreateBrandProfile'
import { showErrorMessageToast } from 'common/MessageToasts/ErrorMessageToast/ErrorMessageToast'
import { BrandProfileWebsiteProps } from './Types'

import { NewWebsite } from 'flows/common/steps/BrandKitForm/BrandKitMultiplePagesForm'
import { useIsUrlCrawlable } from 'hooks/useIsUrlCrawlable'
import { useCreateSource } from 'hooks/queries/workspaces/useCreateSource'
import { useCreateBrandKitSource } from 'hooks/queries/useCreateBrandKitSource'
import { useCheckHostReachable } from 'hooks/useCheckHostReachable'
import { useModals } from 'almanac-ui/Modal'
import { ConfirmModal } from 'blaze-components/ConfirmModal/ConfirmModal'

import dayjs from 'dayjs'
import timezone from 'dayjs/plugin/timezone'
import { useAuth } from 'hooks/UseAuth'
import { Text } from 'blaze-ui/Text/Text'
import { DEFAULT_LANGUAGE, LanguageInput } from 'components/Wizards/Wizard/Form/LanguageInputField/LanguageInput'
import { Heading } from 'blaze-ui/Heading/Heading'
import { MediaObject } from 'types'
import { useOnboardingTracking } from 'hooks/useOnboardingTracking'
import { BRAND_KIT_CREATED, trackEvent } from 'utils/trackingUtils'
import { useMemberProfile } from 'hooks/useMemberProfile'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { useFlipperFlag } from 'hooks/useFlipperFlag'
import { useUpdateIterableProfile } from 'hooks/queries/useUpdateIterableProfile'
import { useOnboardingStrategiesEnabled } from 'flows/virtualMarketerOnboarding/steps'
import { MultiStepStepLayout } from 'components/MultiStepFlow/MultiStepStepLayout/MultiStepStepLayout'
import { TopBar } from '../TopBar/TopBar'
import { ArrowLeft } from 'blaze-ui/icons/20'
import { IconButton } from 'blaze-ui/IconButton/IconButton'

dayjs.extend(timezone)

const BrandProfileWebsite = ({
  onNext,
  websites: initialWebsites,
  onBack,
  brandKitId,
  language,
  onLanguageChange,
  onNoWebsite,
}: BrandProfileWebsiteProps) => {
  const isOnboardingStrategiesEnabled = useOnboardingStrategiesEnabled()
  const [showMobileWebsiteInput, setShowMobileWebsiteInput] = useState(false)
  const [newUrl, setNewUrl] = useState<string>('') // used for single website input
  const [websites] = useState<NewWebsite[]>( // used for multiple website input
    initialWebsites.map((website, index) => ({ index, url: website.url, favicon: website.favicon })),
  )
  const [selectedLanguage, setSelectedLanguage] = useState<string>(language || DEFAULT_LANGUAGE)
  const [isDirty, setIsDirty] = useState(false)
  const { isValid } = useIsUrlCrawlable(newUrl.trim())
  const { mutateAsync: updateIterableProfile } = useUpdateIterableProfile()
  const { mutate, isLoading: isLoadingBrandProfile } = useCreateBrandProfile()
  const { mutateAsync: createSource, isLoading: isLoadingSource } = useCreateSource()
  const auth = useAuth()
  const { mutateAsync: createBrandKitSource, isLoading: isLoadingBrandKitSource } = useCreateBrandKitSource()
  const [documents] = useState<MediaObject[]>([])
  const { trackWebsiteUrlSubmitted } = useOnboardingTracking()
  const currentWorkspaceId = useCurrentWorkspaceId()
  const { profile } = useMemberProfile(currentWorkspaceId, auth?.id)
  const isLoading = isLoadingSource || isLoadingBrandKitSource || isLoadingBrandProfile
  const { isMobile } = useResponsiveDesign()
  const isNoWebsiteFlowEnabled = useFlipperFlag('onboarding_no_website_flow')
  const checkHostReachable = useCheckHostReachable()
  const { openModal } = useModals()

  const handleSubmit = async (url?: string, documents?: MediaObject[]) => {
    const submittedUrl = url ? url.trim() : websites[0]?.url

    if (submittedUrl) {
      const reachable = await checkHostReachable(submittedUrl)
      if (!reachable) {
        const confirmed = await new Promise<boolean>((resolve) => {
          openModal(ConfirmModal, {
            title: "We couldn't reach this website",
            prompt: 'Please double-check the URL for typos. Do you want to continue anyway?',
            cancelText: 'Edit URL',
            confirmText: 'Continue anyway',
            onConfirm: () => resolve(true),
            onCancel: () => resolve(false),
          })
        })
        if (!confirmed) {
          setIsDirty(false)
          return
        }
      }
      trackWebsiteUrlSubmitted(submittedUrl)
    }

    if (brandKitId) {
      await Promise.all(
        (url ? [{ index: 0, url }] : websites).map(async (website) => {
          const source = await createSource({ url: website.url, type: 'Website' })
          return await createBrandKitSource({
            brandKitId,
            source: { type: 'Website', id: source.source.id },
          })
        }),
      )
    }

    mutate(
      {
        url: newUrl ? newUrl.trim() : websites[0].url,
        document_media_object_ids: documents?.map((doc) => doc.id) || [],
        brandKitId,
        language: selectedLanguage,
        timezone: dayjs.tz.guess(),
      },
      {
        onSuccess: (data) => {
          trackEvent(BRAND_KIT_CREATED, profile)
          updateIterableProfile({ onboarding_device: isMobile ? 'mobile' : 'desktop' })
          onNext(data.brand_profile, undefined)
        },
        onError: (error) => {
          showErrorMessageToast(error.message)
        },
      },
    )
  }

  if (isOnboardingStrategiesEnabled) {
    if (isMobile) {
      if (showMobileWebsiteInput) {
        return (
          <div className={Styles.mobileWebsiteInputContainer}>
            <div className={Styles.mobileWebsiteInputHeader}>
              <IconButton
                icon={() => <ArrowLeft size={24} />}
                onPress={() => setShowMobileWebsiteInput(false)}
                variant="tertiary"
              />
            </div>
            <Heading level={1}>Enter your website to get started</Heading>
            <Input
              size="xl"
              name="website"
              type="url"
              placeholder="Enter your website..."
              value={newUrl}
              onChange={(e) => setNewUrl(e.target.value)}
              autoFocus={!isMobile}
              autoComplete="off"
              autoCapitalize="off"
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  setIsDirty(true)
                  if (isValid) {
                    handleSubmit()
                  }
                } else {
                  setIsDirty(false)
                }
              }}
            />
            <div className={Styles.languageInputWrapperWithLabel}>
              <Text variant="label" color="var(--dark-60)">
                Content language
              </Text>
              <LanguageInput
                onLanguageChange={(language) => {
                  setSelectedLanguage(language)
                  onLanguageChange(language)
                }}
                languageValue={selectedLanguage}
                size="md"
                wrapperClassName={Styles.languageInputWrapper}
                popoverClassName={Styles.languageInputPopover}
              />
            </div>
            <div className={Styles.mobileStrategiesFooter}>
              <Button
                size="lg"
                isDisabled={isLoading || isDirty || !isValid || newUrl === ''}
                onPress={() => {
                  setIsDirty(true)
                  if (isValid) {
                    handleSubmit(newUrl, documents)
                  }
                }}
                fullWidth
              >
                Analyze my business
              </Button>
            </div>
          </div>
        )
      }

      return (
        <div className={Styles.mobileStrategiesContainer}>
          <Heading level={1}>You're 7 minutes away from a full content plan.</Heading>
          <div className={Styles.strategiesGalleryContainer}>
            <div className={Styles.galleryItem}>
              <img
                src="https://res.cloudinary.com/almanac/image/upload/v1774290901/blaze_assets/virtual_marketer/onboarding/cards3_nfm4w7.png"
                alt="gallery1"
              />
            </div>
            <div className={Styles.galleryItem}>
              <img
                src="https://res.cloudinary.com/almanac/image/upload/v1774290901/blaze_assets/virtual_marketer/onboarding/cards4_dfystt.png"
                alt="gallery2"
              />
            </div>
            <div className={Styles.galleryItem}>
              <img
                src="https://res.cloudinary.com/almanac/image/upload/v1774290899/blaze_assets/virtual_marketer/onboarding/cards1_deuhlj.png"
                alt="gallery3"
              />
            </div>
            <div className={Styles.galleryItem}>
              <img
                src="https://res.cloudinary.com/almanac/image/upload/v1774290899/blaze_assets/virtual_marketer/onboarding/cards2_jakmsk.png"
                alt="gallery4"
              />
            </div>
          </div>
          <div className={Styles.mobileStrategiesFooter}>
            <Button size="lg" onPress={() => setShowMobileWebsiteInput(true)} fullWidth>
              Get started
            </Button>
          </div>
        </div>
      )
    }

    return (
      <div className={Styles.strategiesContainer}>
        <TopBar highlightStep="yourBusiness" />
        <MultiStepStepLayout
          title={<div>You're 7 minutes away from a full content plan.</div>}
          centerContent
          description="Just add your website. We handle the rest."
          scrollContainerClassName={Styles.scrollContainer}
        >
          <div className={Styles.websiteInputWrapper}>
            <Input
              size="xl"
              name="website"
              type="url"
              placeholder="Enter your website..."
              value={newUrl}
              onChange={(e) => setNewUrl(e.target.value)}
              autoFocus={!isMobile}
              autoComplete="off"
              autoCapitalize="off"
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  setIsDirty(true)
                  if (isValid) {
                    handleSubmit()
                  }
                } else {
                  setIsDirty(false)
                }
              }}
            />
            <Button
              id="brand-profile-website-next"
              aria-label="Submit"
              size="xl"
              onPress={() => {
                setIsDirty(true)
                if (isValid) {
                  handleSubmit(newUrl, documents)
                }
              }}
              isDisabled={isLoading || isDirty || !isValid || newUrl === ''}
            >
              Analyze my business
            </Button>
          </div>

          <div className={Styles.languageInputWrapperWithLabel}>
            <Text variant="secondary" color="var(--dark-60)">
              What language should we generate your content in?
            </Text>
            <LanguageInput
              onLanguageChange={(language) => {
                setSelectedLanguage(language)
                onLanguageChange(language)
              }}
              languageValue={selectedLanguage}
              size="md"
              wrapperClassName={Styles.languageInputWrapper}
              popoverClassName={Styles.languageInputPopover}
            />
          </div>

          <div className={Styles.strategiesGalleryContainer}>
            <div className={Styles.galleryItem}>
              <img
                src="https://res.cloudinary.com/almanac/image/upload/v1772474922/blaze_assets/virtual_marketer/onboarding/Frame_1171277330_m6ki89.png"
                alt="gallery1"
              />
              <Heading level={4}>
                Analyze your business & <br /> competitors
              </Heading>
            </div>
            <div className={Styles.galleryItem}>
              <img
                src="https://res.cloudinary.com/almanac/image/upload/v1772474920/blaze_assets/virtual_marketer/onboarding/Frame_11712773302_c7mlqy.png"
                alt="gallery2"
              />
              <Heading level={4}>
                Build a content plan to <br /> grow
              </Heading>
            </div>
            <div className={Styles.galleryItem}>
              <img
                src="https://res.cloudinary.com/almanac/image/upload/v1772474921/blaze_assets/virtual_marketer/onboarding/Frame_11712773303_tucr9r.png"
                alt="gallery3"
              />
              <Heading level={4}>Define your visual brand</Heading>
            </div>
            <div className={Styles.galleryItem}>
              <img
                src="https://res.cloudinary.com/almanac/image/upload/v1772474921/blaze_assets/virtual_marketer/onboarding/Frame_11712773304_x47mwh.png"
                alt="gallery4"
              />
              <Heading level={4}>
                Publish your first week of <br /> content
              </Heading>
            </div>
          </div>
        </MultiStepStepLayout>
      </div>
    )
  }

  return (
    <>
      <div className={Styles.pageContainer}>
        <div className={Styles.leftContent}>
          <div className={Styles.profileWebsiteContainer}>
            <div className={Styles.title}>
              <Heading level={1}>Enter your website to get started</Heading>
              <Text variant={isMobile ? 'secondary' : undefined} color={isMobile ? 'var(--dark-40)' : 'var(--dark-60)'}>
                Blaze scans your website to understand what makes your brand unique
              </Text>
            </div>
            <div className={Styles.websiteInputWrapper}>
              <Input
                size="xl"
                name="website"
                type="url"
                placeholder="Enter your website..."
                value={newUrl}
                onChange={(e) => setNewUrl(e.target.value)}
                autoFocus={!isMobile}
                autoComplete="off"
                autoCapitalize="off"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    setIsDirty(true)
                    if (isValid) {
                      handleSubmit()
                    }
                  } else {
                    setIsDirty(false)
                  }
                }}
              />
              {isMobile ? (
                <div className={Styles.languageFieldContainer}>
                  <Text variant="label" color="var(--dark-60)">
                    Content language
                  </Text>
                  <LanguageInput
                    onLanguageChange={(language) => {
                      setSelectedLanguage(language)
                      onLanguageChange(language)
                    }}
                    languageValue={selectedLanguage}
                    size="md"
                    wrapperClassName={Styles.languageInputWrapper}
                    popoverClassName={Styles.languageInputPopover}
                  />
                </div>
              ) : (
                <LanguageInput
                  onLanguageChange={(language) => {
                    setSelectedLanguage(language)
                    onLanguageChange(language)
                  }}
                  languageValue={selectedLanguage}
                  size="md"
                  wrapperClassName={Styles.languageInputWrapper}
                  popoverClassName={Styles.languageInputPopover}
                />
              )}
              <Button
                id="brand-profile-website-next"
                aria-label="Submit"
                size="xl"
                onPress={() => {
                  setIsDirty(true)
                  if (isValid) {
                    handleSubmit(newUrl, documents)
                  }
                }}
                isDisabled={isLoading || isDirty || !isValid || newUrl === ''}
              >
                Build My Profile
              </Button>
            </div>
          </div>

          {isNoWebsiteFlowEnabled && (
            <div className={Styles.noWebsiteFlowContainer}>
              <Text variant="secondary" color="var(--dark-40)">
                Don't have a website?
              </Text>
              <Button variant="tertiary" endIcon={ChevronRight} onPress={onNoWebsite}>
                Set up manually
              </Button>
            </div>
          )}
        </div>
        <div className={Styles.blazeIllustration}>
          <img
            alt="blaze illustration"
            src="https://res.cloudinary.com/almanac/image/upload/v1766336090/blaze_assets/virtual_marketer/onboarding/Frame_1171276883_ugvrcz.png"
          />
        </div>
      </div>
    </>
  )
}

export { BrandProfileWebsite }
